import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Stethoscope,
  ArrowLeft,
  Upload,
  Video as VideoIcon,
  Mic,
  Sparkles,
  Loader2,
  CheckCircle2,
  Trash2,
  Square,
  Captions,
  Film,
  Palette,
  ExternalLink,
  Copy,
  Download,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { formatApiError } from "../lib/utils";
import { supabaseClient } from "../lib/supabaseClient";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const POSITION_OPTIONS = [
  { value: "bottom-left", label: "Bottom Left" },
  { value: "bottom-right", label: "Bottom Right" },
  { value: "top-left", label: "Top Left" },
  { value: "top-right", label: "Top Right" },
];

// Temporary fallback script used when no saved script is selected.
const DEFAULT_TEMP_SCRIPT = {
  id: null,
  topic: "Healthy bites",
  hook: "Looking to spice up your diet with some healthy bites?",
  explanation:
    "1) Lentils: Packed with protein and fiber, lentils are great for heart health. 2) Spinach: Full of vitamins and minerals, spinach is a versatile leafy green that can boost your meals. 3) Turmeric: Known for its anti-inflammatory properties, adding a pinch of turmeric to your dishes can offer numerous benefits.",
};
const FOOTAGE_SCRIPTS = {
  english:
    "Hello, and welcome. My name is Doctor [Your Name], and I have been practising for several years, helping my patients live healthier lives. Today I want to talk about something simple but powerful: the small habits that shape our health every single day. Most people think good health comes from big changes. But in my experience, it is the little things that matter most. Drinking enough water. Sleeping on time. Walking every day. Eating fresh, home-cooked food. These small steps, done consistently, can prevent many common problems. I always tell my patients: do not wait to fall sick to start caring for your body. Prevention is far better, and far easier, than cure. So take a deep breath, smile, and let us begin this journey towards better health together. Thank you for trusting me, and let us take the first step, today.",
  hinglish:
    "Namaste, aur aapka swaagat hai. Main hoon Doctor [Aapka Naam], aur pichhle kai saalon se main apne patients ki health ka dhyaan rakh raha hoon. Aaj main aapse ek simple lekin bahut important baat karna chahta hoon: hamari daily choti choti aadatein, jo hamari sehat ko banati ya bigaadti hain. Log sochte hain ki achhi health ke liye bade bade changes chahiye. Lekin mere experience mein, sabse zyada farak choti cheezein daalti hain. Paani theek se peena. Time par sona. Roz thoda walk karna. Fresh, ghar ka khaana khaana. Ye choti steps, agar rozana ki jaayein, to bahut saari common bimariyon se bacha sakti hain. Main hamesha apne patients ko kehta hoon: bimaar padne ka intezaar mat kijiye. Prevention hamesha cure se behtar hai. To ek gehri saans lijiye, muskuraaiye, aur chaliye is health journey ki shuruaat saath mein karte hain. Dhanyavaad.",
};

const LOOK_PRESETS = [
  {
    key: "clinic",
    label: "Clinic (white coat)",
    prompt:
      "Tight vertical head-and-shoulders portrait of the doctor in a clean white coat with a stethoscope, a bright modern clinic softly blurred behind, soft even lighting",
  },
  {
    key: "studio",
    label: "Studio",
    prompt:
      "Tight vertical head-and-shoulders portrait in smart business-casual attire, a clean seamless studio backdrop behind, professional studio lighting",
  },
  {
    key: "podcast",
    label: "Podcast",
    prompt:
      "Tight vertical head-and-shoulders portrait in a smart blazer, a cozy podcast studio with warm ambient lighting softly blurred behind, relaxed mood",
  },
  {
    key: "formal",
    label: "Formal office",
    prompt:
      "Tight vertical head-and-shoulders portrait in a formal suit, a modern office softly blurred behind, warm professional lighting",
  },
  {
    key: "casual",
    label: "Casual outdoor",
    prompt:
      "Tight vertical head-and-shoulders portrait in smart casual clothes, a pleasant green garden softly blurred behind, natural daylight, friendly",
  },
];

function buildSpokenScriptText(script) {
  if (!script) return "";
  const parts = [];
  const pushPart = (p) => {
    if (p == null) return;
    if (typeof p === "string" || typeof p === "number") parts.push(p);
    else {
      try {
        parts.push(JSON.stringify(p));
      } catch {
        parts.push(String(p));
      }
    }
  };
  pushPart(script.hook);
  pushPart(script.explanation);
  return parts.join(" ").trim();
}

function isValidScript(obj) {
  if (!obj || typeof obj !== "object") return false;
  return (
    typeof obj.topic === "string" ||
    typeof obj.hook === "string" ||
    typeof obj.explanation === "string"
  );
}

function resolveMediaUrl(value) {
  if (!value) return null;
  return value.startsWith("http") ? value : `${BACKEND_URL}${value}`;
}

function AssetCard({
  title,
  description,
  icon: Icon,
  accent,
  kind,
  asset,
  onUpload,
  onDelete,
  uploading,
  extraActions,
  hideUpload,
}) {
  const inputRef = useRef(null);
  const isVideo = kind === "video";

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (file) onUpload(file);
    e.target.value = "";
  };

  const sizeLabel = (bytes) =>
    bytes < 1024 * 1024
      ? `${(bytes / 1024).toFixed(1)} KB`
      : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

  return (
    <div className="bg-white rounded-3xl p-8 border border-[#2C5F44]/10 flex flex-col">
      <div className="flex items-center gap-3 mb-4">
        <div
          className="w-11 h-11 rounded-2xl flex items-center justify-center"
          style={{ backgroundColor: `${accent}15`, color: accent }}
        >
          <Icon className="w-6 h-6" strokeWidth={1.5} />
        </div>
        <div>
          <h3
            className="text-xl font-bold text-[#1C2A21]"
            style={{ fontFamily: "Nunito, sans-serif" }}
          >
            {title}
          </h3>
          <p className="text-sm text-[#5C7063]">{description}</p>
        </div>
        {asset && (
          <CheckCircle2
            className="w-5 h-5 text-[#2C5F44] ml-auto"
            strokeWidth={2}
            data-testid={`avatar-${kind}-ready`}
          />
        )}
      </div>

      {asset ? (
        <div className="flex-1 flex flex-col">
          {isVideo ? (
            <video
              src={`${BACKEND_URL}${asset.download_url}`}
              controls
              className="w-full rounded-2xl mb-4 bg-black aspect-video object-contain"
              data-testid="avatar-video-preview"
            />
          ) : (
            <audio
              src={`${BACKEND_URL}${asset.download_url}`}
              controls
              className="w-full mb-4"
              data-testid="avatar-voice-preview"
            />
          )}
          <p className="text-xs text-[#5C7063] mb-4 truncate">
            {asset.original_filename} • {sizeLabel(asset.size)}
          </p>
          <div className="flex gap-2 mt-auto">
            <Button
              onClick={() => inputRef.current?.click()}
              variant="outline"
              className="flex-1 rounded-full border-[#2C5F44]/20"
              disabled={uploading}
              data-testid={`avatar-${kind}-replace-btn`}
            >
              <Upload className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Replace
            </Button>
            <Button
              onClick={() => onDelete(asset)}
              variant="outline"
              className="rounded-full border-red-200 text-red-600 hover:bg-red-50"
              disabled={uploading}
              data-testid={`avatar-${kind}-delete-btn`}
            >
              <Trash2 className="w-4 h-4" strokeWidth={1.5} />
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex-1 border-2 border-dashed border-[#2C5F44]/20 rounded-2xl p-8 text-center flex flex-col items-center justify-center min-h-[220px]">
          <Icon
            className="w-12 h-12 mx-auto mb-3"
            style={{ color: accent }}
            strokeWidth={1.5}
          />
          <p className="text-sm text-[#5C7063] mb-4">
            {hideUpload
              ? "Use your camera to record a 2+ minute clip"
              : isVideo
                ? "MP4, MOV, or WebM (Max 100MB)"
                : "MP3, WAV, or M4A (Max 25MB)"}
          </p>
          <div className="flex flex-col sm:flex-row gap-2 items-center justify-center">
            {!hideUpload && (
              <Button
                onClick={() => inputRef.current?.click()}
                className="button-pill bg-[#2C5F44] hover:bg-[#244b36] text-white rounded-full"
                disabled={uploading}
                data-testid={`avatar-${kind}-upload-btn`}
              >
                {uploading ? (
                  <Loader2
                    className="w-4 h-4 mr-2 animate-spin"
                    strokeWidth={1.5}
                  />
                ) : (
                  <Upload className="w-4 h-4 mr-2" strokeWidth={1.5} />
                )}
                {uploading ? "Uploading…" : "Choose File"}
              </Button>
            )}
            {extraActions}
          </div>
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={isVideo ? "video/*" : "audio/*"}
        onChange={handleFile}
        className="hidden"
        data-testid={`avatar-${kind}-input`}
      />
    </div>
  );
}

function BrandingPanel({ doctorId }) {
  const [branding, setBranding] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios
      .get(`${BACKEND_URL}/api/doctors/${doctorId}/branding`)
      .then((r) => setBranding(r.data))
      .catch(() =>
        setBranding({
          clinic_name: "",
          tagline: "",
          primary_color: "#2C5F44",
          accent_color: "#D98A6C",
          position: "bottom-left",
        }),
      );
  }, [doctorId]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/doctors/${doctorId}/branding`,
        branding,
      );
      toast.success("Branding saved");
    } catch {
      toast.error("Failed to save branding");
    } finally {
      setSaving(false);
    }
  };

  if (!branding) {
    return (
      <div className="p-6">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }

  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div className="space-y-4">
        <div>
          <Label htmlFor="clinic-name">Clinic / Doctor Name</Label>
          <Input
            id="clinic-name"
            value={branding.clinic_name || ""}
            onChange={(e) =>
              setBranding({ ...branding, clinic_name: e.target.value })
            }
            placeholder="HeartCare Mumbai"
            data-testid="branding-clinic-name"
          />
        </div>
        <div>
          <Label htmlFor="tagline">Tagline / Specialty</Label>
          <Input
            id="tagline"
            value={branding.tagline || ""}
            onChange={(e) =>
              setBranding({ ...branding, tagline: e.target.value })
            }
            placeholder="Cardiology Specialist"
            data-testid="branding-tagline"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Primary Color</Label>
            <div className="flex items-center gap-2 mt-1">
              <input
                type="color"
                value={branding.primary_color || "#2C5F44"}
                onChange={(e) =>
                  setBranding({ ...branding, primary_color: e.target.value })
                }
                className="h-10 w-14 rounded cursor-pointer border border-[#2C5F44]/20"
                data-testid="branding-primary-color"
              />
              <span className="text-sm text-[#5C7063] font-mono">
                {branding.primary_color}
              </span>
            </div>
          </div>
          <div>
            <Label>Accent Color</Label>
            <div className="flex items-center gap-2 mt-1">
              <input
                type="color"
                value={branding.accent_color || "#D98A6C"}
                onChange={(e) =>
                  setBranding({ ...branding, accent_color: e.target.value })
                }
                className="h-10 w-14 rounded cursor-pointer border border-[#2C5F44]/20"
                data-testid="branding-accent-color"
              />
              <span className="text-sm text-[#5C7063] font-mono">
                {branding.accent_color}
              </span>
            </div>
          </div>
        </div>
        <div>
          <Label>Watermark Position</Label>
          <div className="grid grid-cols-2 gap-2 mt-1">
            {POSITION_OPTIONS.map((p) => (
              <button
                key={p.value}
                onClick={() => setBranding({ ...branding, position: p.value })}
                className={`px-3 py-2 rounded-lg text-sm border transition ${
                  branding.position === p.value
                    ? "bg-[#2C5F44] text-white border-[#2C5F44]"
                    : "bg-white text-[#1C2A21] border-[#2C5F44]/20 hover:bg-[#2C5F44]/5"
                }`}
                data-testid={`branding-position-${p.value}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
        <Button
          onClick={save}
          disabled={saving}
          className="button-pill bg-[#2C5F44] hover:bg-[#244b36] text-white rounded-full"
          data-testid="branding-save-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
          Save Branding
        </Button>
      </div>

      {/* Live preview */}
      <div className="bg-[#1C2A21] rounded-2xl aspect-video relative overflow-hidden flex items-center justify-center">
        <div className="text-white/30 text-sm">Video preview area</div>
        <div
          className={`absolute m-4 px-4 py-2 rounded-xl flex items-center gap-2 backdrop-blur-md ${
            branding.position === "bottom-left"
              ? "bottom-0 left-0"
              : branding.position === "bottom-right"
                ? "bottom-0 right-0"
                : branding.position === "top-left"
                  ? "top-0 left-0"
                  : "top-0 right-0"
          }`}
          style={{
            backgroundColor: `${branding.primary_color}D9`,
            borderLeft: `3px solid ${branding.accent_color}`,
          }}
          data-testid="branding-watermark-preview"
        >
          <Stethoscope className="w-4 h-4 text-white" strokeWidth={2} />
          <div>
            <div className="text-white text-xs font-bold leading-tight">
              {branding.clinic_name || "Your Clinic"}
            </div>
            {branding.tagline && (
              <div className="text-white/80 text-[10px] leading-tight">
                {branding.tagline}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function VideoWorkspacePage() {
  const { doctorId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [script, setScript] = useState(null);

  const [video, setVideo] = useState(null);
  const [voice, setVoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadingKind, setUploadingKind] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState(null);
  const [lookInfo, setLookInfo] = useState(null);

  const [recording, setRecording] = useState(false);
  const [faceRecording, setFaceRecording] = useState(false);
  const [cameraOn, setCameraOn] = useState(false);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const faceRecorderRef = useRef(null);
  const faceRecordedChunksRef = useRef([]);
  const faceStreamRef = useRef(null);
  const facePreviewRef = useRef(null);
  const [countdown, setCountdown] = useState(null);
  const [recordSeconds, setRecordSeconds] = useState(0);
  const recordTimerRef = useRef(null);
  const recordCancelRef = useRef(false);
  const teleprompterRef = useRef(null);
  const teleScrollRef = useRef(null);
  const [teleSpeed, setTeleSpeed] = useState(10);
  const [teleFont, setTeleFont] = useState(28);
  const cameraBoxRef = useRef(null);
  const [isFs, setIsFs] = useState(false);

  const [activeTab, setActiveTab] = useState("captions");
  const [teleLang, setTeleLang] = useState("hinglish");
  const [consentInfo, setConsentInfo] = useState(null);
  const [consentBusy, setConsentBusy] = useState(false);
  const [showConsentInline, setShowConsentInline] = useState(false);
  const [consentSubmitted, setConsentSubmitted] = useState(false);
  const consentWinRef = useRef(null);
  const [savedGroupId, setSavedGroupId] = useState(null);
  const [baseLookId, setBaseLookId] = useState("");
  const [lookGroupId, setLookGroupId] = useState("");
  const [looks, setLooks] = useState([]);
  const [lookBusy, setLookBusy] = useState(false);
  const [customPrompt, setCustomPrompt] = useState("");
  const [videoLook, setVideoLook] = useState(null);
  const [videoScript, setVideoScript] = useState("");
  const [voices, setVoices] = useState([]);
  const [selectedVoiceId, setSelectedVoiceId] = useState("");
  const [videoBusy, setVideoBusy] = useState(false);
  const [videoInfo, setVideoInfo] = useState(null);
  const videoFormRef = useRef(null);
  const [videoAspect, setVideoAspect] = useState("9:16");
  const [videoResolution, setVideoResolution] = useState("720p");
  const [libraryVideos, setLibraryVideos] = useState([]);
  const [libraryBusy, setLibraryBusy] = useState(false);
  const [activeSection, setActiveSection] = useState(
    new URLSearchParams(window.location.search).get("tab") === "create"
      ? "create"
      : "studio",
  );
  const [isTeam, setIsTeam] = useState(false);
  const [teamChecked, setTeamChecked] = useState(false);

  const ready = !!video;
  const spokenScriptText = buildSpokenScriptText(script);

  const avatarLooks = Array.isArray(lookInfo?.data)
    ? lookInfo.data
    : Array.isArray(lookInfo?.looks)
      ? lookInfo.looks
      : [];

  const [selectedPreview, setSelectedPreview] = useState(null);

  const fetchLookInfo = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/heygen/avatar-look/${doctorId}`,
      );
      setLookInfo(response.data);
    } catch {
      setLookInfo(null);
    }
  };

  useEffect(() => {
    const incomingScript = location.state?.script;

    if (incomingScript) {
      if (isValidScript(incomingScript)) {
        setScript(incomingScript);
        localStorage.setItem(
          `selectedVideoScript:${doctorId}`,
          JSON.stringify(incomingScript),
        );
      } else {
        setScript(DEFAULT_TEMP_SCRIPT);
      }
      return;
    }

    try {
      const storedScript = localStorage.getItem(
        `selectedVideoScript:${doctorId}`,
      );
      if (storedScript) {
        const parsed = JSON.parse(storedScript);
        if (isValidScript(parsed)) setScript(parsed);
        else setScript(DEFAULT_TEMP_SCRIPT);
      } else {
        // If no script is selected, use a temporary placeholder so scripts
        // are optional but the reading prompt still shows useful text.
        setScript(DEFAULT_TEMP_SCRIPT);
      }
    } catch {
      setScript(DEFAULT_TEMP_SCRIPT);
    }
  }, [doctorId, location.state?.script]);

  useEffect(() => {
    axios
      .get(`${BACKEND_URL}/api/avatar/${doctorId}`)
      .then((r) => {
        setVideo(r.data.video);
        setVoice(r.data.voice);
        setSavedGroupId(r.data.avatar_group_id || null);
        if (r.data.consent_status) {
          setConsentInfo((p) => ({ ...(p || {}), consent_status: r.data.consent_status }));
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [doctorId]);

  useEffect(() => {
    fetchLookInfo();
  }, [doctorId]);

  useEffect(() => {
    axios
      .get(`${BACKEND_URL}/api/voices`)
      .then((r) => {
        const vs = r.data?.voices || [];
        setVoices(vs);
        if (vs.length && !selectedVoiceId) setSelectedVoiceId(vs[0].voice_id);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const previewEl = facePreviewRef.current;
    if (!previewEl) return;

    if ((cameraOn || faceRecording) && faceStreamRef.current) {
      previewEl.srcObject = faceStreamRef.current;
      previewEl.play().catch(() => {});
    } else if (previewEl.srcObject) {
      previewEl.srcObject = null;
    }

    return () => {
      if (previewEl.srcObject) {
        previewEl.srcObject = null;
      }
    };
  }, [faceRecording, cameraOn]);

  // Auto-scrolling teleprompter: gently scroll the script while recording.
  useEffect(() => {
    if (teleScrollRef.current) {
      clearInterval(teleScrollRef.current);
      teleScrollRef.current = null;
    }
    if (!faceRecording) return;
    const el = teleprompterRef.current;
    if (el) el.scrollTop = 0;
    const stepMs = 40;
    teleScrollRef.current = setInterval(() => {
      const node = teleprompterRef.current;
      if (!node) return;
      const remaining = node.scrollHeight - node.clientHeight - node.scrollTop;
      if (remaining <= 0) return; // reached the end, hold
      node.scrollTop += (teleSpeed * stepMs) / 1000;
    }, stepMs);
    return () => {
      if (teleScrollRef.current) {
        clearInterval(teleScrollRef.current);
        teleScrollRef.current = null;
      }
    };
  }, [faceRecording, teleSpeed]);

  // Fullscreen for the camera/teleprompter box.
  useEffect(() => {
    const onFsChange = () => setIsFs(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  const toggleFullscreen = () => {
    const el = cameraBoxRef.current;
    if (!el) return;
    if (document.fullscreenElement) {
      document.exitFullscreen?.();
    } else {
      (el.requestFullscreen || el.webkitRequestFullscreen)?.call(el);
    }
  };

  const uploadFile = async (file, kind) => {
    const maxSize = kind === "video" ? 100 * 1024 * 1024 : 25 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(
        `${kind === "video" ? "Video" : "Voice"} must be < ${maxSize / (1024 * 1024)}MB`,
      );
      return;
    }
    setUploadingKind(kind);
    try {
      const form = new FormData();
      form.append("doctor_id", doctorId);
      form.append("kind", kind);
      form.append("file", file);
      const res = await axios.post(`${BACKEND_URL}/api/avatar/upload`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (kind === "video") setVideo(res.data);
      else setVoice(res.data);
      toast.success(`${kind === "video" ? "Video" : "File"} uploaded`);
    } catch (e) {
      console.error(e);
      toast.error(formatApiError(e.response?.data?.detail, "Upload failed"));
    } finally {
      setUploadingKind(null);
    }
  };

  const deleteAsset = async (asset) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/avatar/${asset.id}`);
      if (asset.kind === "video") setVideo(null);
      else setVoice(null);
      toast.success("Removed");
    } catch {
      toast.error("Delete failed");
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      recordedChunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data.size > 0) recordedChunksRef.current.push(e.data);
      };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(recordedChunksRef.current, {
          type: "audio/webm",
        });
        const file = new File([blob], `voice-${Date.now()}.webm`, {
          type: "audio/webm",
        });
        await uploadFile(file, "voice");
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
      toast.info("Recording... speak a 20-30s sample");
    } catch {
      toast.error("Microphone permission denied");
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setRecording(false);
  };

  const openCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      faceStreamRef.current = stream;
      if (facePreviewRef.current) {
        facePreviewRef.current.srcObject = stream;
        facePreviewRef.current.play().catch(() => {});
      }
      recordCancelRef.current = false;
      setCameraOn(true);
      toast.info("Camera on. Get comfortable, then press Start recording.");
    } catch {
      toast.error("Camera and microphone permission denied");
      setCameraOn(false);
    }
  };

  const beginRecording = async () => {
    let stream = faceStreamRef.current;
    if (!stream) {
      await openCamera();
      stream = faceStreamRef.current;
      if (!stream) return;
    }
    recordCancelRef.current = false;
    for (let c = 3; c >= 1; c--) {
      if (recordCancelRef.current) return;
      setCountdown(c);
      await new Promise((r) => setTimeout(r, 1000));
    }
    setCountdown(null);
    if (recordCancelRef.current) return;
    const recorder = new MediaRecorder(stream);
    faceRecordedChunksRef.current = [];
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) faceRecordedChunksRef.current.push(event.data);
    };
    recorder.onstop = async () => {
      stream.getTracks().forEach((track) => track.stop());
      faceStreamRef.current = null;
      const blob = new Blob(faceRecordedChunksRef.current, { type: recorder.mimeType || "video/webm" });
      const file = new File([blob], `face-${Date.now()}.webm`, { type: blob.type || "video/webm" });
      await uploadFile(file, "video");
    };
    faceRecorderRef.current = recorder;
    recorder.start();
    setRecordSeconds(0);
    recordTimerRef.current = setInterval(() => setRecordSeconds((sec) => sec + 1), 1000);
    setFaceRecording(true);
    toast.info("Recording. Read the script on screen.");
  };

  const stopFaceRecording = () => {
    recordCancelRef.current = true;
    setCountdown(null);
    if (recordTimerRef.current) {
      clearInterval(recordTimerRef.current);
      recordTimerRef.current = null;
    }
    if (faceRecorderRef.current && faceRecorderRef.current.state !== "inactive") {
      faceRecorderRef.current.stop();
    } else if (faceStreamRef.current) {
      faceStreamRef.current.getTracks().forEach((tr) => tr.stop());
      faceStreamRef.current = null;
    }
    if (facePreviewRef.current) {
      facePreviewRef.current.srcObject = null;
    }
    setFaceRecording(false);
    setCameraOn(false);
  };

  const handleGenerate = async () => {
    if (!ready) {
      toast.error("Upload a face video first");
      return;
    }
    // Allow generation when there isn't a saved script id. If the script has
    // no `id`, include the script text in the request as `script_text` so
    // the backend can optionally consume it without requiring a saved item.
    setGenerating(true);
    setGenerated(null);
    try {
      const payload = {
        doctor_id: doctorId,
        add_subtitles: true,
        add_broll: true,
        add_branding: true,
        format: "vertical",
      };
      if (script?.id) payload.script_id = script.id;
      else
        payload.script_text =
          buildSpokenScriptText(script) || script?.explanation || "";

      // Create the avatar with auto-retry so a transient blip self-recovers
      // (this is what made consent "take time" before — a one-off failure).
      let latest = null;
      for (let tries = 1; tries <= 3; tries += 1) {
        try {
          const res = await axios.post(`${BACKEND_URL}/api/avatar/generate`, payload);
          latest = res.data;
          break;
        } catch (err) {
          const detail = String(err.response?.data?.detail || "").toLowerCase();
          // The avatar may already have been created on a prior try — recover it.
          if (detail.includes("verified avatar") || detail.includes("avatar group") || detail.includes("already")) {
            try {
              const av = await axios.get(`${BACKEND_URL}/api/avatar/${doctorId}`);
              if (av.data?.avatar_group_id) {
                latest = { id: null, status: "processing", heygen: { avatar_group_id: av.data.avatar_group_id } };
                break;
              }
            } catch (e2) {
              /* fall through */
            }
          }
          if (tries >= 3) throw err;
          toast.info("Setting up your avatar… retrying");
          await new Promise((resolve) => setTimeout(resolve, 4000));
        }
      }
      setGenerated(latest);

      let attempt = 0;
      while (
        latest.id &&
        (latest.status === "processing" ||
          latest.status === "pending" ||
          latest.status === "queued")
      ) {
        attempt += 1;
        const delayMs = attempt < 12 ? 5000 : 10000;
        await new Promise((resolve) => setTimeout(resolve, delayMs));
        try {
          const statusRes = await axios.get(
            `${BACKEND_URL}/api/avatar/generate/${latest.id}`,
          );
          latest = statusRes.data;
        } catch (error) {
          const detail = error.response?.data?.detail || "";
          if (detail.toLowerCase().includes("avatar is still being prepared")) {
            continue;
          }
          throw error;
        }
        setGenerated(latest);

        if (attempt >= 120) {
          break;
        }
      }

if (latest.preview_url) {
    toast.success("Digital twin preview ready");
    await fetchLookInfo();
} else if (latest.status === "completed") {
    toast.success("HeyGen video generated");
    await fetchLookInfo();
} else if (latest.status === "failed") {
    toast.error(latest.note || "HeyGen generation failed");
} else {
    toast.info("HeyGen generation is still processing");
}
    } catch (e) {
      console.error(e);
      toast.error(
        formatApiError(e.response?.data?.detail, "Generation failed"),
      );
    } finally {
      setGenerating(false);
    }
  };

  const avatarGroupId =
    generated?.heygen?.avatar_group_id ||
    savedGroupId ||
    lookInfo?.group_id ||
    null;

  const startConsent = async () => {
    if (!avatarGroupId) return;
    // Open the window synchronously inside the click so browsers don't block it.
    const win = window.open("", "heygenConsent", "width=520,height=800");
    consentWinRef.current = win;
    setConsentBusy(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/avatar/consent`, {
        group_id: avatarGroupId,
        reroute_url: window.location.href,
      });
      setConsentInfo(res.data);
      if (res.data?.consent_url) {
        if (win) win.location.href = res.data.consent_url;
        toast.success("Consent opened in a window — read it and submit there.");
      } else {
        if (win) win.close();
        toast.info("Consent already provided for this avatar.");
      }
    } catch (e) {
      if (win) win.close();
      toast.error(formatApiError(e.response?.data?.detail, "Could not start consent"));
    } finally {
      setConsentBusy(false);
    }
  };

  const openConsentWindow = () => {
    if (consentInfo?.consent_url) {
      window.open(consentInfo.consent_url, "heygenConsent", "width=520,height=800");
    }
  };

  const refreshConsent = async () => {
    if (!avatarGroupId) return;
    setConsentBusy(true);
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/avatar/consent-status/${avatarGroupId}`,
      );
      setConsentInfo((prev) => ({ ...(prev || {}), ...res.data }));
      toast.info(`Consent status: ${res.data?.consent_status || "unknown"}`);
    } catch (e) {
      toast.error("Could not check consent status");
    } finally {
      setConsentBusy(false);
    }
  };

  useEffect(() => {
    if (!consentInfo?.consent_url || !avatarGroupId) return undefined;
    const done = ["approved", "accepted", "completed", "complete", "consented", "success", "verified", "trained", "done"];
    const timer = setInterval(async () => {
      try {
        const res = await axios.get(
          `${BACKEND_URL}/api/avatar/consent-status/${avatarGroupId}`,
        );
        const st = String(res.data?.consent_status || "").toLowerCase();
        setConsentInfo((prev) => ({ ...(prev || {}), consent_status: res.data?.consent_status }));
        if (done.includes(st)) {
          clearInterval(timer);
          toast.success("Consent complete — the avatar is ready.");
        }
      } catch (e) {
        /* keep polling */
      }
    }, 8000);
    return () => clearInterval(timer);
  }, [consentInfo?.consent_url, avatarGroupId]);

  useEffect(() => {
    if (!supabaseClient) { setTeamChecked(true); return undefined; }
    let mounted = true;
    supabaseClient.auth.getSession().then(({ data }) => {
      const token = data.session?.access_token;
      if (!token || !mounted) { if (mounted) setTeamChecked(true); return; }
      axios
        .get(`${BACKEND_URL}/api/admin/is-admin`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((r) => {
          if (mounted) setIsTeam(!!r.data?.is_admin);
        })
        .catch(() => {})
        .finally(() => {
          if (mounted) setTeamChecked(true);
        });
    });
    return () => {
      mounted = false;
    };
  }, []);

  const consentDone = consentSubmitted || ["approved", "accepted", "completed", "complete", "consented", "success", "verified", "trained", "done"].includes(
    String(consentInfo?.consent_status || "").toLowerCase(),
  );

  const effectiveBaseLook = baseLookId || generated?.heygen?.avatar_id || "";

  const createLook = async (prompt, styleName) => {
    const base = baseLookId || generated?.heygen?.avatar_id;
    if (!base) {
      toast.error("Add a base avatar ID first (or create your twin by recording).");
      return;
    }
    if (!prompt) {
      toast.error("Describe the look you want.");
      return;
    }
    setLookBusy(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/avatar/looks`, {
        avatar_id: base,
        name: styleName || "Custom look",
        prompt: `${prompt} Vertical 9:16 portrait composition, head and shoulders framing, centered and filling the tall vertical frame, looking straight at the camera.`,
        avatar_group_id: lookGroupId || undefined,
        doctor_id: doctorId,
      });
      const look = res.data;
      if (look?.group_id) setLookGroupId(look.group_id);
      setLooks((prev) => [look, ...prev]);
      toast.success(`Generating "${styleName || "custom"}" look — this can take a minute.`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Could not create look"));
    } finally {
      setLookBusy(false);
    }
  };

  const refreshLooks = async () => {
    const gid = lookGroupId || avatarGroupId;
    if (!gid) {
      toast.info("Create a look first, then refresh.");
      return;
    }
    setLookBusy(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/avatar/looks/${gid}`);
      setLooks(res.data?.looks || []);
      if (!lookGroupId) setLookGroupId(gid);
    } catch (e) {
      toast.error("Could not load looks");
    } finally {
      setLookBusy(false);
    }
  };

  const buildLookPrompt = (prompt) =>
    `${prompt} Vertical 9:16 portrait composition, head and shoulders framing, centered and filling the tall vertical frame, looking straight at the camera.`;

  const generateAllTemplates = async () => {
    const base = baseLookId || generated?.heygen?.avatar_id;
    if (!base) {
      toast.error("Record your video first to create your avatar.");
      return;
    }
    setLookBusy(true);
    let gid = lookGroupId || avatarGroupId || undefined;
    let ok = 0;
    let fail = 0;
    try {
      // Generate one at a time (a parallel burst gets throttled by HeyGen and
      // silently drops looks). Retry each once before giving up.
      for (const p of LOOK_PRESETS) {
        let done = false;
        for (let attempt = 1; attempt <= 2 && !done; attempt += 1) {
          try {
            const r = await axios.post(`${BACKEND_URL}/api/avatar/looks`, {
              avatar_id: base,
              name: p.label,
              prompt: buildLookPrompt(p.prompt),
              avatar_group_id: gid,
              doctor_id: doctorId,
            });
            gid = r.data?.group_id || gid;
            if (gid) setLookGroupId(gid);
            setLooks((prev) => [r.data, ...prev]);
            ok += 1;
            done = true;
          } catch (e) {
            if (attempt >= 2) fail += 1;
            else await new Promise((res) => setTimeout(res, 3000));
          }
        }
        // small spacing between looks so HeyGen doesn't rate-limit us
        await new Promise((res) => setTimeout(res, 1500));
      }
      if (fail === 0) {
        toast.success(`All ${ok} templates are generating — images appear below automatically.`);
      } else {
        toast.warning(`${ok} templates started, ${fail} failed — click the missing style buttons to retry.`);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Could not create looks"));
    } finally {
      setLookBusy(false);
    }
  };

  useEffect(() => {
    const gid = lookGroupId || avatarGroupId;
    if (!gid) return undefined;
    let stopped = false;
    let n = 0;
    const fetchOnce = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/avatar/looks/${gid}`);
        const ls = res.data?.looks || [];
        if (ls.length) setLooks(ls);
        return ls.length > 0 && ls.every((l) => l.preview_image_url);
      } catch (e) {
        return false;
      }
    };
    fetchOnce();
    const timer = setInterval(async () => {
      n += 1;
      const allReady = await fetchOnce();
      if (allReady || n >= 24 || stopped) clearInterval(timer);
    }, 15000);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [lookGroupId, avatarGroupId]);

  useEffect(() => {
    const first = Array.isArray(avatarLooks) ? avatarLooks[0] : null;
    const bid = lookInfo?.look_id || (first && first.look_id) || generated?.heygen?.avatar_id || "";
    const gid = lookInfo?.group_id || (first && first.group_id) || generated?.heygen?.avatar_group_id || "";
    if (bid) setBaseLookId((prev) => prev || bid);
    if (gid) setLookGroupId((prev) => prev || gid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookInfo, avatarLooks]);

  const makeLookVideo = async () => {
    if (!videoLook) return;
    if (!videoScript.trim()) {
      toast.error("Write what the doctor should say.");
      return;
    }
    setVideoBusy(true);
    setVideoInfo(null);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/avatar/look-video`, {
        avatar_id: videoLook.look_id,
        script: videoScript,
        voice_id: selectedVoiceId || generated?.heygen?.voice_id || undefined,
        title: videoLook.name || "Doctor video",
        aspect_ratio: videoAspect,
        resolution: videoResolution,
        doctor_id: doctorId,
      });
      if (res.data?.video_id) {
        setVideoInfo({ video_id: res.data.video_id, status: "processing" });
        toast.success("Video is rendering — it will appear below when ready.");
      } else {
        toast.error("Could not start the video.");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Could not start video"));
    } finally {
      setVideoBusy(false);
    }
  };

  useEffect(() => {
    const vid = videoInfo?.video_id;
    if (!vid || videoInfo?.video_url) return undefined;
    let n = 0;
    const timer = setInterval(async () => {
      n += 1;
      try {
        const res = await axios.get(`${BACKEND_URL}/api/avatar/video/${vid}`);
        const d = res.data || {};
        const st = String(d.status || "").toLowerCase();
        if (d.video_url || ["completed", "done", "success", "succeeded"].includes(st)) {
          setVideoInfo((prev) => ({ ...(prev || {}), ...d }));
          clearInterval(timer);
        } else if (st === "failed" || n >= 40) {
          setVideoInfo((prev) => ({ ...(prev || {}), ...d }));
          clearInterval(timer);
        }
      } catch (e) {
        /* keep polling */
      }
    }, 12000);
    return () => clearInterval(timer);
  }, [videoInfo?.video_id, videoInfo?.video_url]);

  useEffect(() => {
    if (videoLook && videoFormRef.current) {
      videoFormRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [videoLook]);

  const loadLibrary = async () => {
    setLibraryBusy(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/library/videos/${doctorId}`);
      setLibraryVideos(res.data?.videos || []);
    } catch (e) {
      toast.error("Could not load library");
    } finally {
      setLibraryBusy(false);
    }
  };

  const importFromHeygen = async () => {
    setLibraryBusy(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/library/import/${doctorId}`);
      toast.success(`Imported ${res.data?.imported ?? 0} video(s) from HeyGen.`);
      await loadLibrary();
    } catch (e) {
      toast.error("Import failed");
    } finally {
      setLibraryBusy(false);
    }
  };

  useEffect(() => {
    if (activeSection === "library") loadLibrary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection]);

  const downloadSrt = () => {
    if (!generated?.subtitles?.srt) return;
    const blob = new Blob([generated.subtitles.srt], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${script?.topic || "subtitles"}.srt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] grain-texture">
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => (isTeam ? navigate("/admin") : navigate(-1))}
              variant="ghost"
              size="sm"
              className="rounded-full"
              data-testid="video-back-btn"
            >
              <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
            </Button>
            <div>
              <h1
                className="text-4xl font-bold text-[#1C2A21] mb-2"
                style={{ fontFamily: "Nunito, sans-serif" }}
              >
                Avatar Workspace
              </h1>
              <p className="text-lg text-[#5C7063] max-w-2xl">
                Upload one face video, then turn any script into a polished,
                compliant video — auto-captions, real B-roll, clinic branding
                included.
              </p>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-10 space-y-8">
        {/* Progress */}
        <div
          className="bg-white rounded-3xl p-6 border border-[#2C5F44]/10"
          data-testid="avatar-progress"
        >
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-6">
              {[
                { n: 1, label: "Face Video", done: !!video },
                { n: 2, label: "Generate", done: !!generated, accent: true },
                { n: 3, label: "Edit & Polish", done: !!generated },
              ].map((s) => (
                <div key={s.n} className="flex items-center gap-2">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold ${
                      s.done
                        ? s.accent
                          ? "bg-[#D98A6C] text-white"
                          : "bg-[#2C5F44] text-white"
                        : "bg-[#2C5F44]/10 text-[#2C5F44]"
                    }`}
                  >
                    {s.done ? (
                      <CheckCircle2 className="w-4 h-4" strokeWidth={2.5} />
                    ) : (
                      s.n
                    )}
                  </div>
                  <span className="text-sm font-medium text-[#1C2A21]">
                    {s.label}
                  </span>
                </div>
              ))}
            </div>
            {ready && (
              <span className="text-sm font-semibold text-[#2C5F44]">
                Your clone is ready to use
              </span>
            )}
          </div>
        </div>

        {/* Section tabs */}
        <div className="flex gap-2" data-testid="section-tabs">
          <button type="button" onClick={() => setActiveSection("create")} className={`px-4 py-2 rounded-full text-sm font-semibold ${activeSection === "create" ? "bg-[#2C5F44] text-white" : "bg-[#2C5F44]/10 text-[#2C5F44]"}`} data-testid="section-create">Create Avatar</button>
          {isTeam && (
            <>
              <button type="button" onClick={() => setActiveSection("studio")} className={`px-4 py-2 rounded-full text-sm font-semibold ${activeSection === "studio" ? "bg-[#2C5F44] text-white" : "bg-[#2C5F44]/10 text-[#2C5F44]"}`} data-testid="section-studio">Content Studio</button>
              <button type="button" onClick={() => setActiveSection("library")} className={`px-4 py-2 rounded-full text-sm font-semibold ${activeSection === "library" ? "bg-[#2C5F44] text-white" : "bg-[#2C5F44]/10 text-[#2C5F44]"}`} data-testid="section-library">Library</button>
            </>
          )}
        </div>

        {consentDone && teamChecked && !isTeam && (
          <motion.div className="bg-white rounded-2xl p-6 shadow-md border border-[#2C5F44]/25 mb-4 text-center" data-testid="onboarding-complete">
            <h2 className="text-xl font-bold text-[#1C2A21]">Thank you — you're all set!</h2>
            <p className="text-sm text-[#5C7063] mt-2 max-w-xl mx-auto">Your recording is in and your avatar is being created. There's nothing more for you to do — our team will prepare your videos and share them with you.</p>
            <Button className="mt-4" onClick={async () => { try { await supabaseClient?.auth.signOut(); } catch (e) {} navigate("/auth", { replace: true }); }} data-testid="onboarding-logout-btn">Log out</Button>
          </motion.div>
        )}

        {activeSection === "create" && (
        <>
        {/* Asset cards */}
        {loading ? (
          <div className="bg-white rounded-3xl p-16 border border-[#2C5F44]/10 text-center">
            <Loader2
              className="w-8 h-8 animate-spin mx-auto text-[#2C5F44]"
              strokeWidth={1.5}
            />
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <AssetCard
                title="Face Video"
                description="2+ min clip, good lighting, look at the camera"
                icon={VideoIcon}
                accent="#2C5F44"
                kind="video"
                asset={video}
                hideUpload
                onUpload={(f) => uploadFile(f, "video")}
                onDelete={deleteAsset}
                uploading={uploadingKind === "video"}
                extraActions={
                  !faceRecording ? (
                    <Button
                      onClick={cameraOn ? beginRecording : openCamera}
                      variant="outline"
                      className="rounded-full border-[#2C5F44]/20 text-[#2C5F44] hover:bg-[#2C5F44]/5"
                      data-testid="avatar-face-record-btn"
                    >
                      <VideoIcon className="w-4 h-4 mr-2" strokeWidth={1.5} />
                      {cameraOn ? "Start recording" : "Open camera"}
                    </Button>
                  ) : (
                    <Button
                      onClick={stopFaceRecording}
                      className="rounded-full bg-red-500 hover:bg-red-600 text-white animate-pulse"
                      data-testid="avatar-face-stop-btn"
                    >
                      <Square className="w-4 h-4 mr-2" strokeWidth={1.5} />
                      Stop Recording
                    </Button>
                  )
                }
              />

              {(cameraOn || faceRecording) && (
                <div ref={cameraBoxRef} className={`bg-black overflow-hidden relative ${isFs ? "w-screen h-screen rounded-none border-0" : "rounded-2xl border border-[#2C5F44]/15 w-full max-w-5xl mx-auto"}`}>
                  <div className="px-4 py-2 flex items-center justify-between bg-black/50 text-white text-xs uppercase tracking-[0.18em]">
                    <span>Live Camera Preview</span>
                    <div className="flex items-center gap-3">
                      <span>{countdown !== null ? "Get ready" : faceRecording ? `REC ${Math.floor(recordSeconds / 60)}:${String(recordSeconds % 60).padStart(2, "0")}` : "Camera on"}</span>
                      <button type="button" onClick={toggleFullscreen} className="px-2 py-1 rounded bg-white/20 hover:bg-white/30 normal-case tracking-normal font-semibold" data-testid="fullscreen-btn">{isFs ? "Exit ⤢" : "⛶ Full screen"}</button>
                    </div>
                  </div>
                  <video
                    ref={facePreviewRef}
                    autoPlay
                    muted
                    playsInline
                    className={`w-full object-cover bg-black ${isFs ? "h-screen" : "max-h-[82vh] aspect-video"}`}
                    data-testid="face-recording-preview"
                  />
                  {countdown !== null && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                      <span className="text-white text-7xl font-bold">{countdown}</span>
                    </div>
                  )}
                  {/* Auto-scrolling teleprompter — near the top so the doctor reads toward the camera */}
                  <div
                    ref={teleprompterRef}
                    className="absolute top-12 left-0 right-0 h-[40%] overflow-hidden bg-black/35 text-white font-semibold px-5 whitespace-pre-line text-center"
                    style={{ fontSize: `${teleFont}px`, lineHeight: 1.5, textShadow: "0 1px 4px rgba(0,0,0,0.9)" }}
                    data-testid="teleprompter-overlay"
                  >
                    <div className="max-w-md mx-auto" style={{ paddingTop: "0.5em", paddingBottom: "80%", lineHeight: 1.7 }}>
                      {FOOTAGE_SCRIPTS[teleLang]}
                    </div>
                  </div>
                  {/* Teleprompter controls */}
                  <div className="absolute bottom-4 right-3 flex flex-col gap-2 bg-black/60 rounded-xl p-2.5 text-white text-xs z-30">
                    <div className="flex items-center gap-1.5">
                      <span className="w-10">Speed</span>
                      <button type="button" onClick={() => setTeleSpeed((s) => Math.max(3, s - 3))} className="w-6 h-6 rounded bg-white/20">−</button>
                      <span className="w-7 text-center font-bold tabular-nums" data-testid="tele-speed-val">{teleSpeed}</span>
                      <button type="button" onClick={() => setTeleSpeed((s) => Math.min(120, s + 3))} className="w-6 h-6 rounded bg-white/20">+</button>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="w-10">Text</span>
                      <button type="button" onClick={() => setTeleFont((f) => Math.max(16, f - 2))} className="w-6 h-6 rounded bg-white/20">−</button>
                      <span className="w-7 text-center font-bold tabular-nums" data-testid="tele-font-val">{teleFont}</span>
                      <button type="button" onClick={() => setTeleFont((f) => Math.min(60, f + 2))} className="w-6 h-6 rounded bg-white/20">+</button>
                    </div>
                    {faceRecording && (
                      <button type="button" onClick={() => { if (teleprompterRef.current) teleprompterRef.current.scrollTop = 0; }} className="rounded bg-white/20 py-1">Restart ↺</button>
                    )}
                  </div>
                  {/* In-view record controls (work in fullscreen too) */}
                  {countdown === null && (
                    <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-30">
                      {!faceRecording ? (
                        <button
                          type="button"
                          onClick={beginRecording}
                          className="px-7 py-3 rounded-full bg-red-600 hover:bg-red-700 text-white font-bold shadow-lg flex items-center gap-2"
                          data-testid="inview-start-record"
                        >
                          <span className="w-3 h-3 rounded-full bg-white inline-block" /> Start recording
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={stopFaceRecording}
                          className="px-7 py-3 rounded-full bg-white hover:bg-gray-100 text-red-600 font-bold shadow-lg flex items-center gap-2"
                          data-testid="inview-stop-record"
                        >
                          <span className="w-3 h-3 rounded-sm bg-red-600 inline-block" /> Stop recording
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="bg-[#2C5F44]/5 rounded-2xl border border-[#2C5F44]/10 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles
                    className="w-4 h-4 text-[#2C5F44]"
                    strokeWidth={1.8}
                  />
                  <span className="text-sm font-semibold text-[#1C2A21]">
                    Read this while recording
                  </span>
                </div>
                {(
                  <div>
                    <div className="flex gap-2 mb-3">
                      <button type="button" onClick={() => setTeleLang("english")} className={`px-3 py-1 rounded-full text-xs font-semibold ${teleLang === "english" ? "bg-[#2C5F44] text-white" : "bg-[#2C5F44]/10 text-[#2C5F44]"}`}>English</button>
                      <button type="button" onClick={() => setTeleLang("hinglish")} className={`px-3 py-1 rounded-full text-xs font-semibold ${teleLang === "hinglish" ? "bg-[#2C5F44] text-white" : "bg-[#2C5F44]/10 text-[#2C5F44]"}`}>Hinglish</button>
                    </div>
                    <p className="text-sm text-[#1C2A21] leading-6 whitespace-pre-line">
                      {FOOTAGE_SCRIPTS[teleLang]}
                    </p>
                    <p className="text-xs text-[#5C7063] mt-3">Speak naturally, look at the camera, and keep talking for at least 2 minutes for a good HeyGen result.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {ready && !avatarGroupId && !consentDone && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl p-6 shadow-md border-2 border-[#2C5F44]/40 mb-4"
            data-testid="create-avatar-step"
          >
            <h3 className="text-lg font-bold text-[#1C2A21]">Step 2 — create your avatar</h3>
            <p className="text-sm text-[#5C7063] mt-1">Your video is uploaded. Now build your talking avatar — this takes a few minutes, then you'll approve a quick consent.</p>
            <Button className="mt-3" onClick={handleGenerate} disabled={generating} data-testid="create-avatar-btn">
              {generating ? "Creating your avatar…" : "Create my avatar"}
            </Button>
          </motion.div>
        )}

        {/* Consent — client-facing standalone step */}
        {avatarGroupId && !consentDone && (
          <motion.div className="bg-white rounded-2xl p-6 shadow-md border-2 border-[#2C5F44]/40 mb-4" data-testid="consent-section">
            <h3 className="text-lg font-bold text-[#1C2A21]">Final step — give consent</h3>
            <p className="text-sm text-[#5C7063] mt-1">A small window opens. Read it, scroll to the bottom and submit. <strong>Then close that window and click “I've given my consent” below.</strong></p>
            {!consentInfo?.consent_url ? (
              <Button className="mt-3" onClick={startConsent} disabled={consentBusy} data-testid="consent-start-btn">
                {consentBusy ? "Opening…" : "Give consent"}
              </Button>
            ) : (
              <div className="mt-3">
                <Button
                  className="w-full sm:w-auto mb-3"
                  onClick={() => { try { consentWinRef.current?.close(); } catch (e) {} refreshConsent(); setConsentSubmitted(true); }}
                  data-testid="consent-finish-btn"
                >
                  I've given my consent — finish
                </Button>
                <div className="flex flex-wrap items-center gap-3">
                  <Button variant="outline" onClick={openConsentWindow} data-testid="consent-window-btn">Reopen consent window</Button>
                  <button type="button" onClick={() => setShowConsentInline((v) => !v)} className="text-sm text-[#2C5F44] underline">
                    {showConsentInline ? "Hide" : "Or view it on this page"}
                  </button>
                  <Button variant="outline" onClick={refreshConsent} disabled={consentBusy} data-testid="consent-refresh-btn">Check status</Button>
                  {consentInfo?.consent_status && (
                    <span className="text-sm font-semibold text-[#2C5F44]">Status: {consentInfo.consent_status}</span>
                  )}
                </div>
                {showConsentInline && (
                  <iframe
                    title="HeyGen consent"
                    src={consentInfo.consent_url}
                    className="w-full rounded-xl border border-[#2C5F44]/20 bg-white mt-3"
                    style={{ height: 560 }}
                    data-testid="consent-iframe"
                  />
                )}
              </div>
            )}
          </motion.div>
        )}

        {/* Generate — team only */}
        {isTeam && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="bg-gradient-to-br from-[#2C5F44] to-[#1f4631] rounded-3xl p-8 text-white shadow-xl shadow-[#2C5F44]/20"
          data-testid="generate-section"
        >
          <div className="flex items-start justify-between flex-wrap gap-6">
            <div className="max-w-xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 text-xs font-semibold rounded-full mb-3">
                <Sparkles className="w-3.5 h-3.5" strokeWidth={2} />
                Step 3
              </div>
              <h2
                className="text-2xl font-bold mb-2"
                style={{ fontFamily: "Nunito, sans-serif" }}
              >
                Generate AI Video
              </h2>
              <p className="text-white/80 mb-2">
                {script
                  ? `Script: "${script.topic}". We'll use your cloned avatar to deliver it.`
                  : "Pick a script from your library, then come back to generate."}
              </p>
              {!ready && (
                <p className="text-white/70 text-sm">
                  Upload a face video to unlock generation.
                </p>
              )}
              {ready && !script?.id && (
                <Button
                  onClick={() => navigate(`/library/${doctorId}`)}
                  variant="outline"
                  className="mt-3 rounded-full bg-white/10 border-white/30 text-white hover:bg-white/20"
                  data-testid="open-library-btn"
                >
                  Open Library to pick a script
                </Button>
              )}
            </div>
            <Button
              onClick={handleGenerate}
              disabled={!ready || generating}
              className="button-pill bg-[#D98A6C] hover:bg-[#c77958] text-white rounded-full disabled:opacity-50"
              data-testid="avatar-generate-btn"
            >
              {generating ? (
                <Loader2
                  className="w-4 h-4 mr-2 animate-spin"
                  strokeWidth={1.5}
                />
              ) : (
                <Sparkles className="w-4 h-4 mr-2" strokeWidth={1.5} />
              )}
              {generating ? "Generating…" : "Generate AI Video"}
            </Button>
          </div>

          {/* consent moved to standalone client step above */}
              {generated && (
            <div
              className="mt-6 bg-black/20 rounded-2xl p-4"
              data-testid="generated-preview-block"
            >
              {generated.note && generated.status === "failed" && (
                <div
                  className="mb-4 px-4 py-3 bg-amber-100/90 text-amber-900 rounded-xl text-sm flex items-start gap-2"
                  data-testid="generated-mock-banner"
                >
                  <Sparkles
                    className="w-4 h-4 flex-shrink-0 mt-0.5"
                    strokeWidth={1.5}
                  />
                  <span>
                    <strong>Status:</strong> {generated.note}
                  </span>
                </div>
              )}
              {avatarLooks.length > 0 && (
                <div className="mb-4">
                  <div className="columns-2 sm:columns-3 md:columns-4 gap-4 space-y-4">
                    {avatarLooks.filter((look) => look.preview_video_url).map((look) => {
                      const vid = resolveMediaUrl(look.preview_video_url);

                      return (
                        <div
                          key={look.look_id}
                          className="break-inside-avoid rounded-lg overflow-hidden border border-white/6 bg-black/10 p-2"
                        >
                          <video
                            src={vid}
                            controls
                            className="w-full rounded-lg bg-black"
                          />

                          <div className="mt-2 text-xs text-white/70 truncate">
                            {look.name || look.look_id}
                          </div>

                          <a
                            href={vid}
                            target="_blank"
                            rel="noreferrer"
                            className="text-sm text-white/70 underline"
                          >
                            Open video preview
                          </a>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {selectedPreview && (
                <div
                  className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-6"
                  onClick={() => setSelectedPreview(null)}
                >
                  <div
                    className="max-w-[90vw] max-h-[90vh] bg-black rounded-lg overflow-hidden"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="p-3 flex items-start justify-end">
                      <button
                        onClick={() => setSelectedPreview(null)}
                        className="text-white/80 px-2 py-1 rounded hover:bg-white/5"
                        aria-label="Close preview"
                      >
                        Close
                      </button>
                    </div>
                    <div className="p-4 flex items-center justify-center">
                      {selectedPreview.vid ? (
                        <video
                          src={selectedPreview.vid}
                          controls
                          autoPlay
                          className="max-h-[80vh] max-w-[86vw] rounded-md bg-black"
                        />
                      ) : selectedPreview.img ? (
                        <img
                          src={selectedPreview.img}
                          alt={selectedPreview.look_id}
                          className="max-h-[80vh] max-w-[86vw] rounded-md object-contain"
                        />
                      ) : (
                        <div className="text-white p-6">
                          No preview available
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {generated.thumbnail_url && !generated.preview_url && (
                <div className="mb-4">
                  <img
                    src={
                      generated.thumbnail_url.startsWith("http")
                        ? generated.thumbnail_url
                        : `${BACKEND_URL}${generated.thumbnail_url}`
                    }
                    alt="HeyGen thumbnail"
                    className="w-full rounded-xl bg-black object-cover"
                    data-testid="ai-generated-thumbnail"
                  />
                </div>
              )}
              {generated.preview_url ? (
                <>
                  <video
                    src={
                      generated.preview_url.startsWith("http")
                        ? generated.preview_url
                        : `${BACKEND_URL}${generated.preview_url}`
                    }
                    controls
                    className="w-full rounded-xl bg-black"
                    data-testid="ai-generated-video"
                  />
                  <div className="grid sm:grid-cols-3 gap-3 mt-4 text-sm">
                    <div className="bg-white/10 rounded-xl p-3">
                      <div className="text-white/60 text-xs uppercase">
                        Captions
                      </div>
                      <div className="font-semibold">
                        {generated.subtitles?.cue_count ?? 0} cues
                      </div>
                    </div>
                    <div className="bg-white/10 rounded-xl p-3">
                      <div className="text-white/60 text-xs uppercase">
                        B-Roll
                      </div>
                      <div className="font-semibold">
                        {generated.broll?.suggestions?.length ?? 0} clips
                      </div>
                    </div>
                    <div className="bg-white/10 rounded-xl p-3">
                      <div className="text-white/60 text-xs uppercase">
                        Branding
                      </div>
                      <div className="font-semibold truncate">
                        {generated.branding?.clinic_name || "—"}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <Button
                      onClick={() =>
                        navigate(`/export/${doctorId}`, { state: { script } })
                      }
                      className="button-pill bg-white text-[#2C5F44] hover:bg-gray-100 rounded-full"
                      data-testid="avatar-export-btn"
                    >
                      Proceed to Export
                    </Button>
                    {generated.video_page_url && (
                      <Button
                        asChild
                        variant="outline"
                        className="rounded-full border-white/30 bg-white/10 text-white hover:bg-white/20"
                        data-testid="avatar-video-page-link"
                      >
                        <a
                          href={generated.video_page_url}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Open HeyGen Page
                        </a>
                      </Button>
                    )}
                  </div>
                </>
              ) : (
                <div className="rounded-xl border border-white/15 bg-white/10 px-4 py-6 text-white/80">
                  HeyGen is rendering the video now. This usually takes a little
                  while.
                  {generated.video_page_url && (
                    <div className="mt-3">
                      <a
                        href={generated.video_page_url}
                        target="_blank"
                        rel="noreferrer"
                        className="underline underline-offset-4 text-white"
                      >
                        Open the HeyGen render page
                      </a>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </motion.div>
        )}
              {isTeam && avatarLooks.length > 0 && (
                <div className="bg-white rounded-3xl p-8 border border-[#2C5F44]/10">
                  <h2
                    className="text-2xl font-bold text-[#1C2A21] mb-6"
                    style={{ fontFamily: "Nunito, sans-serif" }}
                  >
                    Previous Avatars
                  </h2>

                  <div className="grid md:grid-cols-3 gap-6">
                    {avatarLooks.filter((look) => look.preview_video_url).map((look, index) => {
                      const videoUrl = resolveMediaUrl(look.preview_video_url);

                      return (
                        <div
                          key={look.look_id || look.id || index}
                          className="rounded-2xl border border-[#2C5F44]/10 overflow-hidden bg-[#FDFBF7]"
                        >
                          <video
                            src={videoUrl}
                            controls
                            playsInline
                            preload="metadata"
                            className="w-full aspect-video bg-black object-contain"
                          />

                          <div className="p-4">
                            <p className="font-semibold text-[#1C2A21]">
                              {look.name || `Avatar ${index + 1}`}
                            </p>

                            <p className="text-xs text-[#5C7063]">
                              {look.status || "completed"}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

        {/* Edit & Polish panel — team only */}
        {isTeam && (
        <div
          className="bg-white rounded-3xl border border-[#2C5F44]/10 overflow-hidden"
          data-testid="edit-polish-panel"
        >
          <div className="p-6 border-b border-[#2C5F44]/10 flex items-center justify-between flex-wrap gap-3">
            <div>
              <h2
                className="text-2xl font-bold text-[#1C2A21]"
                style={{ fontFamily: "Nunito, sans-serif" }}
              >
                Edit &amp; Polish
              </h2>
              <p className="text-sm text-[#5C7063]">
                Review the captions, B-roll suggestions and branding that will
                be applied at generation.
              </p>
            </div>
            <div className="flex gap-2">
              {[
                { id: "captions", label: "Captions", icon: Captions },
                { id: "broll", label: "B-Roll", icon: Film },
                { id: "branding", label: "Branding", icon: Palette },
              ].map((t) => (
                <button
                  key={t.id}
                  onClick={() => setActiveTab(t.id)}
                  className={`px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2 transition ${
                    activeTab === t.id
                      ? "bg-[#2C5F44] text-white"
                      : "bg-[#2C5F44]/10 text-[#2C5F44] hover:bg-[#2C5F44]/20"
                  }`}
                  data-testid={`edit-tab-${t.id}`}
                >
                  <t.icon className="w-4 h-4" strokeWidth={1.5} />
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div className="p-6">
            {activeTab === "captions" && (
              <CaptionsTab generated={generated} onDownload={downloadSrt} />
            )}
            {activeTab === "broll" && <BrollTab generated={generated} />}
            {activeTab === "branding" && <BrandingPanel doctorId={doctorId} />}
          </div>
        </div>
        )}
        </>
        )}

        {activeSection === "studio" && (
        <>
              <motion.div className="bg-white rounded-2xl p-5 shadow-md border border-[#2C5F44]/10 mb-4" data-testid="templates-section">
                <h3 className="text-lg font-bold text-[#1C2A21]">Templates (looks)</h3>
                <p className="text-sm text-[#5C7063] mt-1">Generate the same doctor in different outfits and settings. Each look is reusable across all future videos.</p>
                <Button
                  className="mt-4 w-full sm:w-auto"
                  disabled={lookBusy}
                  onClick={generateAllTemplates}
                  data-testid="templates-generate-all"
                >
                  Generate all 5 templates
                </Button>
                <p className="text-xs text-[#5C7063] mt-2">Images appear below automatically as they finish — no need to refresh.</p>
                {!effectiveBaseLook && (
                  <p className="text-sm text-[#5C7063] mt-3" data-testid="templates-need-avatar">Record your video first — your avatar is detected automatically and your look templates appear here. Nothing to paste.</p>
                )}
                <p className="text-xs font-semibold text-[#5C7063] mt-4">Or pick individual styles:</p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {LOOK_PRESETS.map((preset) => (
                    <Button
                      key={preset.key}
                      variant="outline"
                      disabled={lookBusy}
                      onClick={() => createLook(preset.prompt, preset.label)}
                      data-testid={`template-preset-${preset.key}`}
                    >
                      {preset.label}
                    </Button>
                  ))}
                </div>
                <div className="flex flex-wrap gap-2 mt-4">
                  <input
                    value={customPrompt}
                    onChange={(e) => setCustomPrompt(e.target.value)}
                    placeholder="Custom style, e.g. traditional Indian kurta in a clinic"
                    className="flex-1 min-w-[220px] rounded-xl border border-[#2C5F44]/20 px-3 py-2 text-sm"
                    data-testid="templates-custom-input"
                  />
                  <Button disabled={lookBusy} onClick={() => createLook(customPrompt, "Custom look")} data-testid="template-custom-btn">Generate custom</Button>
                  <Button variant="outline" disabled={lookBusy} onClick={refreshLooks} data-testid="templates-refresh-btn">Refresh looks</Button>
                </div>
                {looks.length > 0 && (
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2 mt-4">
                    {looks.map((look, i) => (
                      <div key={look.look_id || i} className="rounded-xl border border-[#2C5F44]/10 overflow-hidden bg-[#2C5F44]/5" data-testid="template-look-card">
                        {look.preview_image_url ? (
                          <img src={look.preview_image_url} alt={look.name || "look"} className="w-full aspect-square object-cover" />
                        ) : (
                          <div className="w-full aspect-square flex items-center justify-center text-xs text-[#5C7063] p-2 text-center">Generating… tap Refresh looks</div>
                        )}
                        <div className="px-2 py-1 text-xs text-[#1C2A21] truncate">{look.name || "Look"}</div>
                        <button
                          type="button"
                          onClick={() => { setVideoLook(look); setVideoInfo(null); }}
                          disabled={!look.preview_image_url}
                          className="w-full text-xs font-semibold py-1 bg-[#2C5F44] text-white disabled:opacity-40"
                          data-testid="look-make-video"
                        >
                          Make video
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
              {videoLook && (
                <motion.div ref={videoFormRef} className="bg-white rounded-2xl p-5 shadow-md border-2 border-[#2C5F44]/40 mb-4" data-testid="look-video-section">
                  <h3 className="text-lg font-bold text-[#1C2A21]">Make a video — {videoLook.name || "Look"}</h3>
                  <p className="text-sm text-[#5C7063] mt-1">Type what the doctor should say. The video comes out in this look.</p>
                  <p className="text-xs font-semibold text-[#5C7063] mt-3">Output size</p>
                  <div className="flex gap-2 mt-1">
                    <button
                      type="button"
                      onClick={() => setVideoAspect("9:16")}
                      className={`px-3 py-1 rounded-full text-xs font-semibold ${videoAspect === "9:16" ? "bg-[#2C5F44] text-white" : "bg-[#2C5F44]/10 text-[#2C5F44]"}`}
                      data-testid="aspect-reel"
                    >
                      Portrait · Reel (9:16)
                    </button>
                    <button
                      type="button"
                      onClick={() => setVideoAspect("16:9")}
                      className={`px-3 py-1 rounded-full text-xs font-semibold ${videoAspect === "16:9" ? "bg-[#2C5F44] text-white" : "bg-[#2C5F44]/10 text-[#2C5F44]"}`}
                      data-testid="aspect-long"
                    >
                      Landscape (16:9)
                    </button>
                  </div>
                  <p className="text-xs font-semibold text-[#5C7063] mt-3">Quality</p>
                  <div className="flex gap-2 mt-1">
                    <button
                      type="button"
                      onClick={() => setVideoResolution("720p")}
                      className={`px-3 py-1 rounded-full text-xs font-semibold ${videoResolution === "720p" ? "bg-[#2C5F44] text-white" : "bg-[#2C5F44]/10 text-[#2C5F44]"}`}
                      data-testid="res-720"
                    >
                      Faster (720p)
                    </button>
                    <button
                      type="button"
                      onClick={() => setVideoResolution("1080p")}
                      className={`px-3 py-1 rounded-full text-xs font-semibold ${videoResolution === "1080p" ? "bg-[#2C5F44] text-white" : "bg-[#2C5F44]/10 text-[#2C5F44]"}`}
                      data-testid="res-1080"
                    >
                      Sharper (1080p)
                    </button>
                  </div>
                  <p className="text-xs font-semibold text-[#5C7063] mt-3">Voice</p>
                  <select
                    value={selectedVoiceId}
                    onChange={(e) => setSelectedVoiceId(e.target.value)}
                    className="mt-1 w-full rounded-xl border border-[#2C5F44]/20 px-3 py-2 text-sm bg-white"
                    data-testid="voice-select"
                  >
                    {voices.length === 0 && <option value="">Default voice</option>}
                    {voices.map((v) => (
                      <option key={v.voice_id} value={v.voice_id}>
                        {v.language} — {v.name}{v.gender ? ` (${v.gender})` : ""}
                      </option>
                    ))}
                  </select>
                  <p className="text-[11px] text-[#5C7063] mt-1">For Hinglish, pick a <strong>Hindi</strong> voice and write the Hindi words in Devanagari (देवनागरी). English lines can stay in English.</p>
                  <textarea
                    value={videoScript}
                    onChange={(e) => setVideoScript(e.target.value)}
                    rows={4}
                    placeholder="e.g. Namaste, aaj ki health tip: roz subah gunguna paani piyiye..."
                    className="mt-3 w-full rounded-xl border border-[#2C5F44]/20 px-3 py-2 text-sm"
                    data-testid="look-video-script"
                  />
                  <div className="flex flex-wrap gap-3 mt-3">
                    <Button onClick={makeLookVideo} disabled={videoBusy || (videoInfo?.video_id && !videoInfo?.video_url && videoInfo?.status !== "failed")} data-testid="look-video-generate">
                      {(videoBusy || (videoInfo?.video_id && !videoInfo?.video_url && videoInfo?.status !== "failed"))
                        ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" strokeWidth={1.5} />Creating the video…</>)
                        : "Generate video"}
                    </Button>
                    <Button variant="outline" onClick={() => { setVideoLook(null); setVideoInfo(null); }}>Cancel</Button>
                  </div>
                  {(videoBusy || (videoInfo?.video_id && !videoInfo?.video_url && videoInfo?.status !== "failed")) && (
                    <div className="mt-4 flex items-center gap-3 rounded-2xl border-2 border-[#2C5F44]/30 bg-[#2C5F44]/5 px-5 py-4" data-testid="video-rendering">
                      <Loader2 className="w-6 h-6 animate-spin text-[#2C5F44] flex-shrink-0" strokeWidth={2} />
                      <div>
                        <p className="font-bold text-[#1C2A21]">Making your video…</p>
                        <p className="text-sm text-[#5C7063]">HeyGen is rendering it now — this usually takes 1–3 minutes. It will appear here automatically; you can keep working.</p>
                      </div>
                    </div>
                  )}
                  {videoInfo?.status === "failed" && !videoInfo?.video_url && (
                    <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700" data-testid="video-failed">
                      The render didn't complete. Please click <strong>Generate video</strong> again, or try a different look or voice.
                    </div>
                  )}
                  {videoInfo?.video_url && (
                    <div className="mt-4">
                      <video src={videoInfo.video_url} controls className={`rounded-xl mx-auto ${videoAspect === "9:16" ? "w-full max-w-[300px]" : "w-full max-w-2xl"}`} data-testid="look-video-player" />
                      <div className="flex flex-wrap items-center gap-3 mt-2">
                        <a href={`${BACKEND_URL}/api/avatar/video-download/${videoInfo.video_id}`} className="text-sm font-semibold text-white bg-[#2C5F44] rounded-full px-4 py-1.5" data-testid="video-download">Download (full quality)</a>
                        <a href={videoInfo.video_url} target="_blank" rel="noopener noreferrer" className="text-sm text-[#2C5F44] underline">Open in new tab</a>
                      </div>
                    </div>
                  )}
                </motion.div>
              )}

        </>
        )}

        {activeSection === "library" && (
        <>
          <div className="bg-white rounded-2xl p-5 shadow-md border border-[#2C5F44]/10 mb-4" data-testid="library-section">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h3 className="text-lg font-bold text-[#1C2A21]">Content library</h3>
              <div className="flex gap-2">
                <Button onClick={importFromHeygen} disabled={libraryBusy} data-testid="library-import">Import from HeyGen</Button>
                <Button variant="outline" onClick={loadLibrary} disabled={libraryBusy} data-testid="library-refresh">Refresh</Button>
              </div>
            </div>
            <p className="text-sm text-[#5C7063] mt-1">Every video you generate is saved here, with a fresh full-quality download link.</p>
            {libraryVideos.length === 0 ? (
              <p className="text-sm text-[#5C7063] mt-4" data-testid="library-empty">{libraryBusy ? "Loading…" : "No videos yet. Make a video in Content Studio and it will appear here."}</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mt-4">
                {libraryVideos.map((v) => (
                  <div key={v.id} className="rounded-xl border border-[#2C5F44]/10 overflow-hidden bg-[#2C5F44]/5" data-testid="library-item">
                    {v.video_url ? (
                      <video src={v.video_url} controls className="w-full bg-black" />
                    ) : (
                      <div className="w-full aspect-video flex items-center justify-center text-xs text-[#5C7063] text-center px-2">{v.status === "completed" ? "Link expired — tap Refresh" : "Still rendering… tap Refresh"}</div>
                    )}
                    <div className="p-3">
                      <p className="text-sm font-semibold text-[#1C2A21] truncate">{v.title}</p>
                      <p className="text-xs text-[#5C7063]">{v.aspect} · {v.resolution} · {v.status}{v.est_credits ? ` · ≈${v.est_credits} credits` : ""}</p>
                      {v.video_url && (
                        <a href={`${BACKEND_URL}/api/avatar/video-download/${v.video_id}`} className="text-xs font-semibold text-[#2C5F44] underline mt-1 inline-block" data-testid="library-download">Download</a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
        )}
      </div>
    </div>
  );
}

function CaptionsTab({ generated, onDownload }) {
  const cues = generated?.subtitles?.cues;
  if (!cues) {
    return (
      <p className="text-sm text-[#5C7063]" data-testid="captions-empty">
        Captions will appear here after you click{" "}
        <strong>Generate AI Video</strong>. We auto-time them from your script
        at ~150 words/minute.
      </p>
    );
  }
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-[#5C7063]">
          {cues.length} cues • {generated.subtitles.total_duration}s total
        </p>
        <Button
          onClick={onDownload}
          variant="outline"
          size="sm"
          className="rounded-full border-[#2C5F44]/20"
          data-testid="captions-download-srt"
        >
          <Download className="w-4 h-4 mr-2" strokeWidth={1.5} />
          Download .srt
        </Button>
      </div>
      <div
        className="max-h-72 overflow-y-auto space-y-2 pr-2"
        data-testid="captions-list"
      >
        {cues.map((c) => (
          <div
            key={c.index}
            className="flex gap-3 items-start p-3 bg-[#FDFBF7] rounded-xl"
          >
            <span className="text-xs font-mono text-[#5C7063] w-20 flex-shrink-0">
              {c.start.toFixed(1)}–{c.end.toFixed(1)}s
            </span>
            <span className="text-sm text-[#1C2A21]">{c.text}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function BrollTab({ generated }) {
  const suggestions = generated?.broll?.suggestions;
  const [previewClip, setPreviewClip] = useState(null);

  if (!suggestions) {
    return (
      <p className="text-sm text-[#5C7063]" data-testid="broll-empty">
        After you click <strong>Generate AI Video</strong>, we use AI to suggest
        4–6 short B-roll clips matched to your script and auto-fetch real free
        stock from Pexels.
      </p>
    );
  }
  return (
    <div data-testid="broll-list">
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {suggestions.map((s, i) => (
          <div
            key={i}
            className="bg-[#FDFBF7] rounded-xl border border-[#2C5F44]/10 overflow-hidden flex flex-col"
            data-testid={`broll-card-${i}`}
          >
            {s.clip?.thumbnail ? (
              <button
                type="button"
                onClick={() => setPreviewClip(s.clip)}
                className="relative aspect-video bg-black overflow-hidden group"
                data-testid={`broll-thumb-${i}`}
              >
                <img
                  src={s.clip.thumbnail}
                  alt={s.keyword}
                  className="w-full h-full object-cover group-hover:scale-105 transition"
                />
                <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
                  <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
                    <Film className="w-5 h-5 text-[#2C5F44]" strokeWidth={2} />
                  </div>
                </div>
                {s.clip.duration && (
                  <span className="absolute bottom-2 right-2 px-2 py-0.5 bg-black/70 text-white text-xs rounded">
                    {s.clip.duration}s
                  </span>
                )}
              </button>
            ) : (
              <div className="aspect-video bg-[#2C5F44]/5 flex items-center justify-center">
                <Film className="w-8 h-8 text-[#2C5F44]/30" strokeWidth={1.5} />
              </div>
            )}
            <div className="p-3 flex-1 flex flex-col">
              <div className="flex items-start justify-between mb-1">
                <h4 className="font-semibold text-[#1C2A21] text-sm">
                  {s.keyword}
                </h4>
                <span className="text-[10px] font-mono text-[#5C7063] flex-shrink-0 ml-2">
                  {s.start?.toFixed(1)}–{s.end?.toFixed(1)}s
                </span>
              </div>
              <p className="text-xs text-[#5C7063] mb-2 line-clamp-2">
                {s.reason}
              </p>
              {s.clip?.credit_name && (
                <p className="text-[10px] text-[#5C7063]/80 mb-2">
                  Credit:{" "}
                  <a
                    href={s.clip.credit_url || s.clip.page_url}
                    target="_blank"
                    rel="noreferrer"
                    className="underline hover:text-[#2C5F44]"
                  >
                    {s.clip.credit_name}
                  </a>{" "}
                  on Pexels
                </p>
              )}
              <div className="flex gap-2 mt-auto pt-2 border-t border-[#2C5F44]/10">
                {s.clip?.video_url ? (
                  <a
                    href={s.clip.video_url}
                    download
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs font-semibold text-[#2C5F44] hover:underline flex items-center gap-1"
                    data-testid={`broll-download-${i}`}
                  >
                    <Download className="w-3 h-3" /> Download
                  </a>
                ) : (
                  <a
                    href={s.pexels_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs font-semibold text-[#2C5F44] hover:underline flex items-center gap-1"
                    data-testid={`broll-pexels-${i}`}
                  >
                    Pexels <ExternalLink className="w-3 h-3" />
                  </a>
                )}
                <a
                  href={s.pexels_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-[#5C7063] hover:text-[#1C2A21] flex items-center gap-1"
                  data-testid={`broll-more-${i}`}
                >
                  <ExternalLink className="w-3 h-3" /> More
                </a>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(s.keyword);
                    toast.success("Keyword copied");
                  }}
                  className="text-xs text-[#5C7063] hover:text-[#1C2A21] flex items-center gap-1 ml-auto"
                  data-testid={`broll-copy-${i}`}
                >
                  <Copy className="w-3 h-3" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Inline preview overlay */}
      {previewClip && (
        <div
          className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4"
          onClick={() => setPreviewClip(null)}
          data-testid="broll-preview-overlay"
        >
          <div
            className="bg-black rounded-2xl overflow-hidden max-w-3xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <video
              src={previewClip.video_url}
              controls
              autoPlay
              className="w-full max-h-[70vh]"
              data-testid="broll-preview-video"
            />
            <div className="p-4 bg-black text-white text-xs flex justify-between items-center">
              <span>
                Credit:{" "}
                <a
                  href={previewClip.credit_url || previewClip.page_url}
                  target="_blank"
                  rel="noreferrer"
                  className="underline"
                >
                  {previewClip.credit_name || "Pexels"}
                </a>{" "}
                · Free for commercial use
              </span>
              <button
                onClick={() => setPreviewClip(null)}
                className="text-white/70 hover:text-white"
                data-testid="broll-preview-close"
              >
                Close ✕
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
